import { useTranslation } from 'react-i18next';

import { Input, Pagination, Spin } from '@arco-design/web-react';

import { EmptyTips } from '@/components';
import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { RepoSelectorCard } from '@/components-biz/RepoSelectorCard';

import { useAppPlazaRepoList } from './hooks/useAppPlazaRepoList';
import styles from './index.module.less';
import { appPlazaItemToContainerImage, isAppPlazaImageMatch } from './utils';

export { appPlazaItemToContainerImage, isAppPlazaImageMatch } from './utils';
export { useAppPlazaRepoList } from './hooks/useAppPlazaRepoList';

export interface AppPlazaRepoSelectorProps {
  disabled?: boolean;
  /** 当前选中的镜像 id（imageKey） */
  value?: string;
  onChange?: (item: CommonImageItemType) => void;
}

/** 应用广场镜像选择（创建应用实例弹窗用） */
export function AppPlazaRepoSelector({ disabled = false, value, onChange }: AppPlazaRepoSelectorProps) {
  const { t } = useTranslation();
  const { items, loading, page, limit, total, inputValue, onInputChange, onSearch, onPageChange } = useAppPlazaRepoList();

  const handleSelect = (item: (typeof items)[number]) => {
    if (disabled) return;
    const mapped = appPlazaItemToContainerImage(item);
    if (!mapped.id) return;
    onChange?.(mapped);
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchBar}>
        <h5 className={styles.title}>{t('home:market.app.center')}</h5>
        <Input.Search
          placeholder={t('data-center:maas.app.plaza.search')}
          allowClear
          value={inputValue}
          onChange={onInputChange}
          onSearch={onSearch}
          onClear={() => onSearch('')}
          disabled={disabled}
          style={{ width: '40%' }}
        />
      </div>

      <Spin loading={loading} block>
        <div className={styles.repoGrid}>
          {items.map((item) => {
            const selected = isAppPlazaImageMatch(item, value);
            return (
              <RepoSelectorCard
                key={item.id}
                name={item.name}
                metaItems={[item.vendor, item.version]}
                address={item.dockerRefUrl}
                selected={selected}
                disabled={disabled}
                onClick={() => handleSelect(item)}
              />
            );
          })}
        </div>

        {!loading && items.length === 0 ? (
          <div className={styles.emptyWrapper}>
            <EmptyTips tips={t('data-center:maas.app.plaza.empty')} />
          </div>
        ) : null}

        {total > limit ? (
          <div className={styles.paginationWrapper}>
            <Pagination size="mini" current={page} pageSize={limit} total={total} onChange={onPageChange} showTotal disabled={disabled} />
          </div>
        ) : null}
      </Spin>
    </div>
  );
}
